// Who may do what on a quality alert, as the sheet decides it.
//
// Everything about a pod's alerts is the floor's business: anyone signed in
// may read them, move them between the open stages, correct the defect, add a
// photo or a note. CLOSING is the one act Odoo reserves (interpod_quality):
// only a user listed as an approver of the alert's team may move it to a done
// stage, and a team with no approvers is open to anyone. That rule is enforced
// on the write; this module reads the same facts so the sheet can offer the
// close only to the person who can take it, and say why to everyone else.
//
// A kiosk session acts as the shared service account, which is nobody's
// approval — closing from a kiosk is never offered, whoever typed the PIN.

export interface QualitySession {
  /** `user` is a credentials session (a named Odoo account); `kiosk` is the PIN kiosk. */
  connMode: 'none' | 'kiosk' | 'user';
  /** The Odoo uid behind a credentials session; null on a kiosk. */
  userUid: number | null;
  /** The PIN operator (kiosk) or the account's own employee (user); null when read-only. */
  operatorId: number | null;
  isLeader: boolean;
}

/** Anyone identified may edit — stage, category, notes, photos. */
export function canEditAlert(s: QualitySession): boolean {
  return s.isLeader || s.operatorId != null || (s.connMode === 'user' && s.userUid != null);
}

export type CloseVerdict =
  | { ok: true }
  /** A kiosk session: sign in as yourself to close. */
  | { ok: false; reason: 'kiosk' }
  /** A named user who is not on the team's approver list. */
  | { ok: false; reason: 'notApprover' }
  /** Nobody signed in. */
  | { ok: false; reason: 'anonymous' };

/**
 * Whether THIS session may close an alert of a team with these approvers.
 * `approverUids` undefined means the team is not known yet (still loading):
 * answered as not-yet rather than as a refusal nobody has earned.
 */
export function canCloseAlert(s: QualitySession, approverUids: readonly number[] | undefined): CloseVerdict {
  if (s.connMode !== 'user' || s.userUid == null) {
    return s.connMode === 'kiosk' ? { ok: false, reason: 'kiosk' } : { ok: false, reason: 'anonymous' };
  }
  if (approverUids === undefined) return { ok: false, reason: 'notApprover' };
  if (approverUids.length === 0 || approverUids.includes(s.userUid)) return { ok: true };
  return { ok: false, reason: 'notApprover' };
}
